import React from 'react';
import { Box, VStack, HStack, Text, Icon, Link } from '@chakra-ui/react';
import { IconType } from 'react-icons';
import { FiCopy } from 'react-icons/fi';
import { useAlert } from '../../hooks/useAlert';
import { useTranslation } from '../../hooks/useTranslation';

interface ContactInfoItemProps {
    icon: IconType;
    label: string;
    value: string;
    onClick?: () => void;
    href?: string;
    hint?: string;
}

const ContactInfoItem: React.FC<ContactInfoItemProps> = ({ icon, label, value, onClick, href, hint }) => {
    const { success } = useAlert();
    const { t } = useTranslation();

    const handleCopy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            success(t.pages.support.copied);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <Box display="flex" justifyContent="center" onClick={onClick}>
            <Box p={6} bg="white" w="full" maxW={{ base: 'full', md: '500px' }}>
                <HStack gap={4} align="center" justify="space-between">
                    <HStack gap={4} align="center" flex={1}>
                        <Icon as={icon} boxSize={6} color="yellow.600" />
                        <VStack align="start" gap={1} flex={1}>
                            <Text fontSize="md" color="gray.500">
                                {label}
                            </Text>
                            {href ? (
                                <Link
                                    href={href}
                                    fontSize="lg"
                                    fontWeight="medium"
                                    color="gray.900"
                                    _hover={{ color: 'blue.500' }}
                                    transition="color 0.2s"
                                    textDecoration="none"
                                >
                                    {value}
                                </Link>
                            ) : (
                                <Text fontSize="lg" fontWeight="medium" color="gray.900">
                                    {value}
                                </Text>
                            )}
                            {hint && (
                                <Text fontSize="md" color="gray.500" mt={1}>
                                    {hint}
                                </Text>
                            )}
                        </VStack>
                    </HStack>
                    <Box
                        as="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(value);
                        }}
                        p={2}
                        borderRadius="md"
                        _hover={{ bg: 'gray.100' }}
                        transition="background 0.2s"
                        cursor="pointer"
                    >
                        <Icon as={FiCopy} boxSize={5} color="gray.500" />
                    </Box>
                </HStack>
            </Box>
        </Box>
    );
};

export default ContactInfoItem;
